import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, Outlet, useLocation, useMatches } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { ToastProvider } from './contexts/ToastContext'
import ErrorBoundary, { RouteErrorBoundary } from './components/ErrorBoundary'
import ProtectedRoute from './components/ProtectedRoute'
import UnsavedChangesGuard from './components/UnsavedChangesGuard'
import Header from './components/Header'
import Navigation from './components/Navigation'
import MatchesList from './pages/MatchesList'
import MatchDetail from './pages/MatchDetail'
import VodHighlightsList from './pages/VodHighlightsList'
import VodHighlightDetail from './pages/VodHighlightDetail'
import VodsList from './pages/VodsList'
import VodCreate from './pages/VodCreate'
import VodDetail from './pages/VodDetail'
import SeriesList from './pages/SeriesList'
import SeriesCreate from './pages/SeriesCreate'
import SeriesDetail from './pages/SeriesDetail'
import CompetitionsList from './pages/CompetitionsList'
import TournamentCreate from './pages/TournamentCreate'
import TournamentDetail from './pages/TournamentDetail'
import ComplexesList from './pages/ComplexesList'
import ComplexDetail from './pages/ComplexDetail'
import PlayersList from './pages/PlayersList'
import PlayerDetail from './pages/PlayerDetail'
import TeamsList from './pages/TeamsList'
import TeamDetail from './pages/TeamDetail'
import FeedMapping from './pages/FeedMapping'
import FeedsList from './pages/FeedsList'
import FeedDetail from './pages/FeedDetail'
import LiveControl from './pages/LiveControl'
import ArticleDetail from './pages/ArticleDetail'
import ArticlesList from './pages/ArticlesList'
import MvpdProvidersList from './pages/MvpdProvidersList'
import ArtworkTemplatesList from './pages/ArtworkTemplatesList'
import ArtworkTemplateEditor from './pages/ArtworkTemplateEditor'
import PromotionsList from './pages/PromotionsList'
import UsersList from './pages/UsersList'
import RequireRole, { NoRoleAssigned } from './components/RequireRole'
import { useAuth } from './contexts/AuthContext'
import { landingPath, rolesForPath } from './auth/roles'
import PageLayout from './pages/merchandising/PageLayout'
import WidgetLibrary from './pages/merchandising/WidgetLibrary'
import NavigationEditor from './pages/merchandising/NavigationEditor'
import { Banner, SpeedbumpProvider } from './components'
import { WidgetSiteProvider } from './contexts/WidgetSiteContext'
import SiteSelector from './components/merchandising/SiteSelector'
import './App.scss'
import MatchBriefingPage from './pages/MatchBriefingPage'
import CupTeamsList from './pages/CupTeamsList'
import CupTeamDetail from './pages/CupTeamDetail'
import TieDetail from './pages/TieDetail'

// Chrome shared by every route, as a pathless layout route so the whole app
// renders under one data router.
function RootLayout() {
  return (
    <div className="App">
      <UnsavedChangesGuard />
      <ProtectedRoute>
        <Header />
        <Navigation />
        <main className="main-content">
          <RoleGate />
        </main>
      </ProtectedRoute>
    </div>
  )
}

// One gate for every route, keyed by the first path segment (auth/roles.ts),
// so /content/* is gated once at its parent and SiteSelector never mounts
// for a person who cannot use it.
function RoleGate() {
  const { pathname } = useLocation()
  const { roles } = useAuth()
  const notFound = useMatches().some((match) => match.id === NOT_FOUND_ROUTE_ID)
  if (roles.length === 0) return <NoRoleAssigned />
  // Unknown URLs fall to NotFound, not the admin-only default for unmapped pages.
  const required = notFound ? undefined : rolesForPath(pathname)
  if (!required) return <Outlet />
  return (
    <RequireRole roles={required}>
      <Outlet />
    </RequireRole>
  )
}

function Landing() {
  const { roles } = useAuth()
  return <Navigate to={landingPath(roles)} replace />
}

// The /content subtree keeps its descendant <Routes> rather than becoming
// data-router children, so the site picker stays one wrapper around all of
// them instead of being repeated per route.
const contentRoutes = (
  <WidgetSiteProvider><SiteSelector>
    <Routes>
      <Route path="articles" element={<ArticlesList />} />
      <Route path="articles/:articleId" element={<ArticleDetail />} />
      <Route path="widgets" element={<WidgetLibrary />} />
      <Route path="navigation" element={<NavigationEditor />} />
      <Route path="page/:pageId/placement/:placementId" element={<PageLayout />} />
      <Route path="page/:pageId/preview" element={<PageLayout />} />
      <Route path="page/:pageId" element={<PageLayout />} />
      <Route path="placement/:placementId" element={<PageLayout />} />
      <Route path=":pageType/placement/:placementId" element={<PageLayout />} />
      <Route path=":pageType/preview" element={<PageLayout />} />
      <Route path=":pageType" element={<PageLayout />} />
      <Route path="" element={<PageLayout />} />
    </Routes>
  </SiteSelector></WidgetSiteProvider>
)

// A data router without a catch-all takes over the whole page with its own
// 404 screen — no Header, no Navigation — so unknown URLs are caught inside
// the shell.
const NOT_FOUND_ROUTE_ID = 'not-found'

function NotFound() {
  const { pathname } = useLocation()
  return <Banner variant="warning" message={`No page matches ${pathname}.`} />
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    // Without this the router swallows any render error into its own bare
    // "Unexpected Application Error!" screen — the App-level ErrorBoundary
    // wraps the RouterProvider, so it never sees them.
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Landing /> },
      { path: 'live', element: <LiveControl /> },
      { path: 'matches', element: <MatchesList /> },
      { path: 'matches/:matchId', element: <MatchDetail /> },
      { path: 'vod-highlights', element: <VodHighlightsList /> },
      { path: 'vod-highlights/:highlightId', element: <VodHighlightDetail /> },
      { path: 'vods', element: <VodsList key="catalog" /> },
      { path: 'vods/archived', element: <VodsList key="archived" archived /> },
      { path: 'vods/new', element: <VodCreate /> },
      { path: 'vods/:vodId', element: <VodDetail /> },
      { path: 'series', element: <SeriesList /> },
      { path: 'series/new', element: <SeriesCreate /> },
      { path: 'series/:seriesId', element: <SeriesDetail /> },
      { path: 'tournaments', element: <CompetitionsList /> },
      { path: 'tournaments/new', element: <TournamentCreate /> },
      { path: 'tournaments/:tournamentId', element: <TournamentDetail /> },
      { path: 'players', element: <PlayersList /> },
      { path: 'players/:playerId', element: <PlayerDetail /> },
      { path: 'teams', element: <TeamsList /> },
      { path: 'teams/:teamId', element: <TeamDetail /> },
      { path: 'cup-teams', element: <CupTeamsList /> },
      { path: 'cup-teams/:teamId', element: <CupTeamDetail /> },
      { path: 'ties/:tieId', element: <TieDetail /> },
      { path: 'courts', element: <ComplexesList /> },
      { path: 'courts/:complexId', element: <ComplexDetail /> },
      { path: 'feed-mapping', element: <FeedMapping /> },
      { path: 'feeds', element: <FeedsList /> },
      { path: 'feeds/:feedId', element: <FeedDetail /> },
      { path: 'match-briefing', element: <MatchBriefingPage /> },
      { path: 'mvpd-providers', element: <MvpdProvidersList /> },
      { path: 'artwork-templates', element: <ArtworkTemplatesList /> },
      { path: 'artwork-templates/:templateId', element: <ArtworkTemplateEditor /> },
      { path: 'promotions', element: <PromotionsList /> },
      { path: 'users', element: <UsersList /> },
      { path: 'content/*', element: contentRoutes },
      { id: NOT_FOUND_ROUTE_ID, path: '*', element: <NotFound /> },
    ],
  },
])

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <SpeedbumpProvider>
            <RouterProvider router={router} />
          </SpeedbumpProvider>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
